import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface CartLine {
  productId: number;
  name: string;
  image: string;
  unitPrice: number;
  currency: string;
  quantity: number;
  stock: number;
}

interface CartContextValue {
  lines: CartLine[];
  totalItems: number;
  subtotal: number;
  addToCart: (line: Omit<CartLine, "quantity">, qty?: number) => void;
  updateQuantity: (productId: number, qty: number) => void;
  removeLine: (productId: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = "toy-shop-cart-v1";

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as CartLine[];
    } catch (e) {
      console.warn("cart parse fail", e);
    }
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch (e) {
      console.warn("cart save fail", e);
    }
  }, [lines]);

  const addToCart = useCallback((line: Omit<CartLine, "quantity">, qty = 1) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.productId === line.productId);
      if (existing) {
        const newQty = Math.min(existing.quantity + qty, line.stock || 999);
        return prev.map((l) => (l.productId === line.productId ? { ...l, quantity: newQty } : l));
      }
      return [...prev, { ...line, quantity: Math.min(qty, line.stock || 999) }];
    });
  }, []);

  const updateQuantity = useCallback((productId: number, qty: number) => {
    setLines((prev) => {
      if (qty <= 0) return prev.filter((l) => l.productId !== productId);
      return prev.map((l) => (l.productId === productId ? { ...l, quantity: Math.min(qty, l.stock || 999) } : l));
    });
  }, []);

  const removeLine = useCallback((productId: number) => {
    setLines((prev) => prev.filter((l) => l.productId !== productId));
  }, []);

  const clearCart = useCallback(() => setLines([]), []);

  const value = useMemo<CartContextValue>(() => {
    const totalItems = lines.reduce((sum, l) => sum + l.quantity, 0);
    const subtotal = lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
    return { lines, totalItems, subtotal, addToCart, updateQuantity, removeLine, clearCart };
  }, [lines, addToCart, updateQuantity, removeLine, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}